import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Volume2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import type { AudioReport } from '@/lib/schema.types';

const POLL_MS = 3000;

/**
 * Floating "Narration audio" widget for the lesson-pack editor. Shows the live
 * generation report (succeeded/total + the failed segments), and lets the admin
 * (re)generate all clips or retry only the failed ones. Polls while a run is in
 * progress. Pack-only — mounted by EditLessonPackPage.
 */
export function AudioReportPanel({ packId }: { packId: string }) {
  const [report, setReport] = useState<AudioReport | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  // Consecutive 'idle' polls after a trigger. The backend flips to 'running'
  // within ~1s, so if it stays idle a while the job never started — give up
  // rather than poll forever.
  const idleStreak = useRef(0);

  const stop = useCallback(() => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  const fetchReport = useCallback(async () => {
    try {
      const r = await api.lessonPacks.audioReport(packId);
      setReport(r);
      // Only stop on a FINISHED run. A pre-job 'idle' right after triggering
      // must not stop polling (that was the bug) — give the job a grace window
      // to flip to 'running'.
      if (r.status === 'running') {
        idleStreak.current = 0;
      } else if (r.status === 'done') {
        stop();
      } else {
        idleStreak.current += 1;
        if (idleStreak.current >= 12) stop(); // ~36s with no job → give up
      }
      return r;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load audio status');
      stop();
      return null;
    }
  }, [packId, stop]);

  const startPolling = useCallback(() => {
    stop();
    timer.current = setInterval(() => void fetchReport(), POLL_MS);
  }, [fetchReport, stop]);

  // Initial load; keep polling if a run is already in flight.
  useEffect(() => {
    void fetchReport().then((r) => {
      if (r?.status === 'running') startPolling();
    });
    return stop;
  }, [fetchReport, startPolling, stop]);

  const trigger = async (mode: 'all' | 'failed') => {
    setBusy(true);
    setError(null);
    try {
      await api.lessonPacks.generateAudio(packId, mode);
      // Optimistically show 'running' + disable the buttons immediately so a
      // second concurrent run can't be fired. The first poll fills in the real
      // total. No immediate fetch — it would race the not-yet-persisted job and
      // read a stale state (which previously stopped polling).
      idleStreak.current = 0;
      setReport((prev) =>
        prev
          ? { ...prev, status: 'running' }
          : { status: 'running', total: 0, succeeded: 0, failed: 0, failures: [] },
      );
      setOpen(true);
      startPolling();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start audio generation');
    } finally {
      setBusy(false);
    }
  };

  const running = report?.status === 'running';
  const total = report?.total ?? 0;
  const succeeded = report?.succeeded ?? 0;
  const failed = report?.failed ?? 0;
  const pct = total > 0 ? Math.round(((succeeded + failed) / total) * 100) : 0;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 left-4 z-40 flex items-center gap-2 rounded-full border border-border bg-bg-panel px-3 py-1.5 text-[12px] font-medium text-fg shadow-slide hover:bg-bg-subtle"
      >
        {running ? (
          <Loader2 size={13} className="animate-spin text-accent" />
        ) : (
          <Volume2 size={13} className="text-accent" />
        )}
        {running
          ? `Audio ${succeeded + failed}/${total}…`
          : total > 0
            ? `Audio ${succeeded}/${total}`
            : 'Audio'}
        {failed > 0 && !running && <span className="text-red-600">· {failed} failed</span>}
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 z-40 w-[320px] rounded-lg border border-border bg-bg-panel shadow-slide">
      <div className="flex items-center justify-between border-b border-border-subtle px-3 py-2">
        <div className="flex items-center gap-1.5 text-[13px] font-semibold text-fg">
          <Volume2 size={14} className="text-accent" /> Narration audio
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="grid h-6 w-6 place-items-center rounded text-fg-subtle hover:bg-bg-subtle hover:text-fg"
        >
          <X size={14} />
        </button>
      </div>

      <div className="space-y-3 px-3 py-3">
        <div>
          <div className="mb-1 flex items-center justify-between text-[12px]">
            <span className="text-fg-muted">
              {running ? 'Generating…' : report?.status === 'done' ? 'Done' : 'Not generated yet'}
            </span>
            <span className="font-medium text-fg">
              {succeeded}/{total || '—'}
              {failed > 0 && <span className="text-red-600"> · {failed} failed</span>}
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-border-subtle">
            <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {error && (
          <p className="rounded-md bg-red-50 px-2 py-1.5 text-[11px] text-red-700">{error}</p>
        )}

        {report && report.failures.length > 0 && (
          <div className="max-h-44 overflow-y-auto rounded-md border border-border-subtle scroll-thin">
            {report.failures.map((f) => (
              <div
                key={f.stepId}
                className="border-b border-border-subtle px-2 py-1.5 last:border-b-0"
              >
                <div className="flex items-start gap-1.5 text-[11.5px] text-fg">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0 text-red-500" />
                  <span className="font-medium break-words">{f.label}</span>
                </div>
                <p className="mt-0.5 break-words pl-[18px] text-[10.5px] text-fg-subtle">
                  {f.error}
                </p>
              </div>
            ))}
          </div>
        )}

        {report?.status === 'done' && report.failures.length === 0 && total > 0 && (
          <p className="flex items-center gap-1.5 text-[12px] text-emerald-600">
            <CheckCircle2 size={13} /> All {total} clips generated.
          </p>
        )}

        <div className="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            className="flex-1 justify-center"
            disabled={busy || running}
            onClick={() => trigger('all')}
          >
            {running ? <Loader2 size={13} className="animate-spin" /> : <Volume2 size={13} />}
            {total > 0 ? 'Regenerate all' : 'Generate all'}
          </Button>
          <Button
            variant="soft"
            size="sm"
            className="flex-1 justify-center"
            disabled={busy || running || failed === 0}
            onClick={() => trigger('failed')}
          >
            <RefreshCw size={13} /> Retry failed
          </Button>
        </div>

        <p className="text-[10.5px] text-fg-subtle">
          Uses VoxCPM. Missing clips fall back to text-to-speech on the classroom app.
        </p>
      </div>
    </div>
  );
}
